import {
  ApiCallError,
  type ApiEnvelope,
  type CarbonometerSnapshot,
  type CheckoutRequest,
  type CheckoutResponse,
  type ComputeRequest,
  type ComputeResponse,
  type DistanceResponse,
  type EmbedI18nBundle,
  type PublicPaymentStatus,
  type SchemaResponse,
  type SearchCitiesResponse,
  type TransportMode,
} from './types';

export interface ClientConfig {
  baseUrl: string;
  locale?: string;
  fetchImpl?: typeof fetch;
}

export class CalculatorClient {
  private readonly baseUrl: string;
  private readonly locale?: string;
  private readonly fetchImpl: typeof fetch;

  constructor(config: ClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.locale = config.locale;
    this.fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  getSchema(token: string, signal?: AbortSignal): Promise<SchemaResponse> {
    return this.request<SchemaResponse>(
      'GET',
      `/v1/public/calculators/${encodeURIComponent(token)}/schema`,
      undefined,
      signal,
    );
  }

  /**
   * Snapshot do carbonômetro — agregado público pra painéis tipo TV.
   * MVP usa polling no caller (a cada 10s); WS fica pra v1.1+.
   */
  getCarbonometer(token: string, signal?: AbortSignal): Promise<CarbonometerSnapshot> {
    const path = `/v1/public/carbonometer/${encodeURIComponent(token)}` +
      (this.locale ? `?locale=${encodeURIComponent(this.locale)}` : '');
    return this.request<CarbonometerSnapshot>('GET', path, undefined, signal);
  }

  compute(token: string, body: ComputeRequest, signal?: AbortSignal): Promise<ComputeResponse> {
    return this.request<ComputeResponse>(
      'POST',
      `/v1/public/calculators/${encodeURIComponent(token)}/compute`,
      body,
      signal,
    );
  }

  checkout(token: string, body: CheckoutRequest, signal?: AbortSignal): Promise<CheckoutResponse> {
    return this.request<CheckoutResponse>(
      'POST',
      `/v1/public/calculators/${encodeURIComponent(token)}/checkout`,
      body,
      signal,
    );
  }

  /**
   * paymentStatus busca o status atual de uma payment_order — usado pelo
   * polling do iframe modal de checkout. Sem auth (público).
   */
  paymentStatus(orderId: string, signal?: AbortSignal): Promise<PublicPaymentStatus> {
    return this.request<PublicPaymentStatus>(
      'GET',
      `/v1/public/payments/${encodeURIComponent(orderId)}/status`,
      undefined,
      signal,
    );
  }

  /**
   * submitResultSubmitter envia opt-in inline pós-resultado (mockup 15 — Step
   * Submitter foi descontinuado em favor de coleta na tela de resultado).
   * Idempotente: 2ª chamada com mesmo result_id é no-op.
   */
  submitResultSubmitter(
    token: string,
    resultId: string,
    body: { full_name?: string; email: string; consent: boolean; locale?: string },
    signal?: AbortSignal,
  ): Promise<{ ok: boolean }> {
    return this.request<{ ok: boolean }>(
      'POST',
      `/v1/public/calculators/${encodeURIComponent(token)}/results/${encodeURIComponent(resultId)}/submitter`,
      body,
      signal,
    );
  }

  /**
   * searchCities devolve sugestões de cidade pro autocomplete do Step 1.
   * Backend proxy + cache do Nominatim. Country code vem da `event_city_country`
   * da própria instância (caller não precisa informar). Devolve `{suggestions:[]}`
   * em falha do provider — UI degrade gracioso pra input livre.
   */
  searchCities(token: string, q: string, signal?: AbortSignal): Promise<SearchCitiesResponse> {
    return this.request<SearchCitiesResponse>(
      'GET',
      `/v1/public/calculators/${encodeURIComponent(token)}/cities/search?q=${encodeURIComponent(q)}`,
      undefined,
      signal,
    );
  }

  /**
   * computeDistance pede ao backend a distância entre `originCity` (input do
   * user) e a `event_city` configurada na instância. A estratégia depende do
   * modal: rodoviário usa rota real (OSRM) e avião usa linha reta — por isso
   * `mode` importa. Retorna ~km arredondado e `method` dizendo como o número
   * saiu (o helper text do campo é escrito a partir dele).
   */
  computeDistance(
    token: string,
    originCity: string,
    mode?: TransportMode,
    signal?: AbortSignal,
  ): Promise<DistanceResponse> {
    const modeQS = mode ? `&mode=${encodeURIComponent(mode)}` : '';
    return this.request<DistanceResponse>(
      'GET',
      `/v1/public/calculators/${encodeURIComponent(token)}/distance?origin=${encodeURIComponent(originCity)}${modeQS}`,
      undefined,
      signal,
    );
  }

  /**
   * i18nBundle carrega o subset público de chaves de tradução (erros
   * client-side + microcopy) pra o locale dado. Cache 1h via header
   * Cache-Control (HTTP cache do browser).
   */
  i18nBundle(locale: string, signal?: AbortSignal): Promise<EmbedI18nBundle> {
    return this.request<EmbedI18nBundle>(
      'GET',
      `/v1/public/i18n/embed?locale=${encodeURIComponent(locale)}`,
      undefined,
      signal,
    );
  }

  private async request<T>(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (this.locale) {
      headers['Accept-Language'] = this.locale;
    }

    let res: Response;
    try {
      res = await this.fetchImpl(this.baseUrl + path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (e) {
      if ((e as Error).name === 'AbortError') throw e;
      throw new ApiCallError(0, 'network_error', (e as Error).message || 'network_error');
    }

    let envelope: ApiEnvelope<T> | undefined;
    try {
      envelope = (await res.json()) as ApiEnvelope<T>;
    } catch {
      throw new ApiCallError(res.status, 'invalid_response', 'invalid_response');
    }

    if (!res.ok || !envelope?.success) {
      const err = envelope?.error;
      throw new ApiCallError(
        res.status,
        err?.code ?? 'unknown_error',
        err?.message ?? `HTTP ${res.status}`,
        err?.details,
      );
    }
    if (envelope.data === undefined) {
      throw new ApiCallError(res.status, 'empty_response', 'empty_response');
    }
    return envelope.data;
  }
}
